import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { BranchProvider } from './hooks/BranchContext';
import { WSProvider } from './hooks/useWebSocket';
import Login from './pages/auth/Login';
import AppLayout from './components/Layout';

dayjs.locale('ru');

const ManagerDashboard = lazy(() => import('./pages/manager/Dashboard'));
const TaskDetail = lazy(() => import('./pages/manager/TaskDetail'));
const TaskCreate = lazy(() => import('./pages/manager/TaskCreate'));
const AxiomReview = lazy(() => import('./pages/manager/AxiomReview'));
const OneCReview = lazy(() => import('./pages/manager/OneCReview'));
const ClientsList = lazy(() => import('./pages/manager/ClientsList'));
const ClientDetail = lazy(() => import('./pages/manager/ClientDetail'));
const TransportList = lazy(() => import('./pages/manager/TransportList'));
const TransportDetail = lazy(() => import('./pages/manager/TransportDetail'));
const LegalEntities = lazy(() => import('./pages/manager/LegalEntities'));
const History = lazy(() => import('./pages/manager/History'));
const ExpeditorToday = lazy(() => import('./pages/expeditor/TodayPlan'));
const ExpeditorTask = lazy(() => import('./pages/expeditor/TaskCard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminBranches = lazy(() => import('./pages/admin/Branches'));
const AdminParser = lazy(() => import('./pages/admin/Parser'));
const AdminParser1C = lazy(() => import('./pages/admin/Parser1C'));
const AdminCities = lazy(() => import('./pages/admin/Cities'));
const AdminSystem = lazy(() => import('./pages/admin/System'));
const AdminFeedback = lazy(() => import('./pages/admin/Feedback'));

function PrivateRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function Loading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
      <Spin size="large" />
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  const home = user.role === 'expeditor' ? '/expeditor' : '/dashboard';

  return (
    <AppLayout>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Navigate to={home} />} />
          <Route path="/dashboard" element={<PrivateRoute roles={['admin', 'manager']}><ManagerDashboard /></PrivateRoute>} />
          <Route path="/tasks/new" element={<PrivateRoute roles={['admin', 'manager']}><TaskCreate /></PrivateRoute>} />
          <Route path="/tasks/:id" element={<PrivateRoute roles={['admin', 'manager']}><TaskDetail /></PrivateRoute>} />
          <Route path="/axiom-review" element={<PrivateRoute roles={['admin', 'manager']}><AxiomReview /></PrivateRoute>} />
          <Route path="/onec-review" element={<PrivateRoute roles={['admin', 'manager']}><OneCReview /></PrivateRoute>} />
          <Route path="/clients" element={<PrivateRoute roles={['admin', 'manager']}><ClientsList /></PrivateRoute>} />
          <Route path="/clients/:id" element={<PrivateRoute roles={['admin', 'manager']}><ClientDetail /></PrivateRoute>} />
          <Route path="/transport" element={<PrivateRoute roles={['admin', 'manager']}><TransportList /></PrivateRoute>} />
          <Route path="/transport/:id" element={<PrivateRoute roles={['admin', 'manager']}><TransportDetail /></PrivateRoute>} />
          <Route path="/legal-entities" element={<PrivateRoute roles={['admin', 'manager']}><LegalEntities /></PrivateRoute>} />
          <Route path="/history" element={<PrivateRoute roles={['admin', 'manager']}><History /></PrivateRoute>} />
          <Route path="/expeditor" element={<PrivateRoute roles={['expeditor']}><ExpeditorToday /></PrivateRoute>} />
          <Route path="/expeditor/task/:id" element={<PrivateRoute roles={['expeditor']}><ExpeditorTask /></PrivateRoute>} />
          <Route path="/admin/users" element={<PrivateRoute roles={['admin']}><AdminUsers /></PrivateRoute>} />
          <Route path="/admin/branches" element={<PrivateRoute roles={['admin']}><AdminBranches /></PrivateRoute>} />
          <Route path="/admin/parser" element={<PrivateRoute roles={['admin']}><AdminParser /></PrivateRoute>} />
          <Route path="/admin/parser-1c" element={<PrivateRoute roles={['admin']}><AdminParser1C /></PrivateRoute>} />
          <Route path="/admin/cities" element={<PrivateRoute roles={['admin']}><AdminCities /></PrivateRoute>} />
          <Route path="/admin/system" element={<PrivateRoute roles={['admin']}><AdminSystem /></PrivateRoute>} />
          {/* Обратная связь: доступ проверяет API (только разработчик из DEVELOPER_ACCOUNTS) */}
          <Route path="/admin/feedback" element={<AdminFeedback />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

export default function App() {
  return (
    <ConfigProvider locale={ruRU}>
      <BrowserRouter>
        <AuthProvider>
          <BranchProvider>
            <WSProvider>
              <AppRoutes />
            </WSProvider>
          </BranchProvider>
        </AuthProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}
